'use client';

import { type Dispatch, type SetStateAction, useState } from 'react';
import { motion } from 'framer-motion';
import type { Content } from '@prismicio/client';
import { asText } from '@prismicio/helpers';
import SplashButton from './SplashButton';
import { logger } from '@/utils/logger';

interface SelectBtnsProps {
  numTracks: number;
  setSelected: Dispatch<SetStateAction<number>>;
  selected: number;
}

interface CardsProps {
  quotes: Content.QuotesDocument[];
  selected: number;
  setSelected: Dispatch<SetStateAction<number>>;
}

interface CardProps {
  quote: Content.QuotesDocument;
  position: number;
  selected: number;
  setSelected: Dispatch<SetStateAction<number>>;
}

const SelectBtns: React.FC<SelectBtnsProps> = ({ numTracks, setSelected, selected }) => {
  // Only render if there are 2 or more tracks
  if (numTracks < 2) return null;

  return (
    <div className="m-auto mt-8 flex max-w-[400px] gap-1">
      {Array.from(Array(numTracks).keys()).map((n) => (
        <button
          key={n}
          onClick={() => setSelected(n)}
          className="relative h-2 w-full rounded-sm bg-slate-300"
        >
          {selected === n ? (
            <motion.span
              className="absolute bottom-0 left-0 top-0 rounded-sm bg-blue-800"
              initial={{ width: '0%' }}
              animate={{ width: '100%' }}
              transition={{ duration: 5 }}
              onAnimationComplete={() => {
                setSelected(selected === numTracks - 1 ? 0 : selected + 1);
              }}
            />
          ) : (
            <span
              className="absolute bottom-0 left-0 top-0 rounded-sm bg-blue-950"
              style={{ width: selected > n ? '100%' : '0%' }}
            />
          )}
        </button>
      ))}
    </div>
  );
};

const Cards: React.FC<CardsProps> = ({ quotes, selected, setSelected }) => {
  return (
    <div className="relative ml-0 h-[370px] p-4 sm:h-[280px] lg:h-[340px]">
      {quotes.map((quote, i) => (
        <Card quote={quote} key={i} position={i} selected={selected} setSelected={setSelected} />
      ))}
    </div>
  );
};

const Card: React.FC<CardProps> = ({ quote, position, selected, setSelected }) => {
  const scale = position <= selected ? 1 : 1 + 0.015 * (position - selected);
  const offset = position <= selected ? 0 : 95 + (position - selected) * 3;
  const background = position % 2 ? '#2A4F54' : 'white';
  const color = position % 2 ? 'white' : '#2A4F54';

  return (
    <motion.div
      initial={false}
      style={{
        zIndex: position,
        transformOrigin: 'left bottom',
        background,
        color,
      }}
      animate={{
        x: `${offset}%`,
        scale,
      }}
      whileHover={{
        translateX: position === selected ? 0 : -3,
      }}
      transition={{
        duration: 0.25,
        ease: 'easeOut',
      }}
      onClick={() => setSelected(position)}
      className="absolute left-0 top-0 flex min-h-full w-full cursor-pointer flex-col justify-between rounded-md p-8 shadow-md shadow-[rgba(0,0,0,0.1)] lg:p-12"
    >
      <p className="my-0 mt-4 py-0 text-base font-light italic md:my-4 xl:text-lg">
        &quot;{asText(quote.data.full_quote)}&quot;
      </p>
      <div>
        <span className="text-md block font-semibold xl:text-lg">{asText(quote.data.name)}</span>
        <span className="block text-sm opacity-70">
          {quote.data.position}, {quote.data.company}
        </span>
      </div>
    </motion.div>
  );
};

interface StackedCardTestimonialsProps {
  quotes: Content.QuotesDocument[];
  nextPost: Content.SuccessStoryDocument;
  labels: Content.LabelsDocument;
  lang: string;
}

const StackedCardTestimonials: React.FC<StackedCardTestimonialsProps> = ({
  quotes,
  nextPost,
  labels,
  lang,
}) => {
  const [selected, setSelected] = useState(0);

  logger.log(nextPost);

  return (
    <section className="bg-blue bg-gradient-to-bl from-blue-700 to-blue-400 pb-10">
      {quotes.length > 0 && (
        <div className="">
          <div className="grid grid-cols-1 items-center gap-8 overflow-hidden px-4 py-12 md:grid-cols-[30%_70%] md:py-24 lg:gap-4 lg:px-20 xl:grid-cols-[40%_60%]">
            <div className="p-4 text-center">
              <h3 className="m-auto max-w-[400px] text-3xl font-light leading-tight text-white lg:text-3xl xl:text-4xl">
                {quotes[0].data.company}

                <span className="mt-0 block text-3xl opacity-70 sm:mt-2">
                  {labels.data.success_story_says}
                </span>
              </h3>
              <p className="my-4 hidden text-white">
                Hear from our satisfied customers about their experiences.
              </p>
              <SelectBtns numTracks={quotes.length} setSelected={setSelected} selected={selected} />
            </div>
            <Cards quotes={quotes} setSelected={setSelected} selected={selected} />
          </div>
        </div>
      )}
      <div className="container mb-8 mt-0 flex flex-col gap-6 sm:flex-row sm:justify-between">
        <SplashButton
          color="transparent"
          text={`< ${labels.data.success_story_back_to_list}`}
          url={`/${lang}/success`}
          className="block w-full text-left sm:w-auto sm:min-w-60 sm:text-center"
        ></SplashButton>

        {nextPost && (
          <SplashButton
            color="white"
            text={`${labels.data.success_story_next} ${nextPost.data.company || ''} >`}
            url={`/${lang}/success/${nextPost.uid || ''}`}
            className="block w-full sm:w-auto sm:min-w-60 sm:text-center"
          ></SplashButton>
        )}
      </div>
    </section>
  );
};

export default StackedCardTestimonials;
